import React, { useState } from 'react';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import { toast } from 'react-hot-toast';
import Table from '../components/Table.tsx';
import { Search, Edit, Trash, Plus, Shield, User, Mail, Phone, Calendar, Globe } from 'lucide-react';
import Modal from '../components/Modal.tsx';
import { useQueryClient } from '@tanstack/react-query';
import getApiErrorMessage from '../utils/helper.js';
import { useAdmins, useCreateAdmin, useUpdateAdmin, useDeleteAdmin, useAdminStats } from '../hooks/useAdmins.ts';
import { useAvailableRoles } from '../hooks/useRoles.ts';
import { useMembers } from '../hooks/useMembers.ts';
import { useSubgroups } from '../hooks/useSubgroups.ts';
import Select from '../components/ui/Select.tsx';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

function getInitials(name) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

const StatusBadge = ({ is_active }) => (
  <span
    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
      is_active ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-500'
    }`}
  >
    {is_active ? 'Active' : 'Inactive'}
  </span>
);

const RoleBadge = ({ role }) => {
  if (!role) return null;
  
  return (
    <span
      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
        role.name === 'super_admin' ? 'bg-purple-100 text-purple-600' : 
        role.name === 'group_admin' ? 'bg-blue-100 text-blue-600' : 
        'bg-gray-100 text-gray-600'
      }`}
    >
      {role.display_name || role.name}
    </span>
  );
};

const ContactInfo = ({ phone, email }) => (
  <div>
    <div className="text-sm text-gray-900">{phone}</div>
    <div className="text-gray-500 text-sm">{email}</div>
  </div>
);

const AvatarName = ({ name, email }) => (
  <div className="flex items-center gap-3">
    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium">
      {getInitials(name)}
    </div>
    <div className="ml-4">
      <div className="font-medium text-gray-900">{name}</div>
    </div>
  </div>
);

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'group_admin', label: 'Group Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function formatDateDMY(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

const AdminsPage = () => {
  const { can } = usePermissions();
  const isAdmin = useIsAdmin();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [memberSearch, setMemberSearch] = useState("");
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);

  const queryClient = useQueryClient();

  // API hooks
  const { data: adminsData, isLoading: isLoadingAdmins } = useAdmins({
    search: searchQuery,
  });

  const { data: membersData, isLoading: isLoadingMembers } = useMembers({
    search: searchQuery,
    status: selectedStatus,
    sort_field: sortField,
    sort_direction: sortDirection,
  });


  const members = Array.isArray(membersData?.data) ? membersData.data : [];

    const filteredMembers = memberSearch === ""
  ? members
  : members?.filter((m) =>
      m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
      m.phone_number.includes(memberSearch)
    );

  
  const { data: rolesData, isLoading: isLoadingRoles } = useAvailableRoles();
  const { data: statsData } = useAdminStats();
  const { data: subgroupsData } = useSubgroups();
  const villages = subgroupsData?.data?.filter(s => s.type === 'village') || [];
  const createAdmin = useCreateAdmin();
  const updateAdmin = useUpdateAdmin();
  const deleteAdmin = useDeleteAdmin();

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddAdmin = () => {
    setSelectedAdmin({
      name: '',
      phone_number: '',
      role_id: null,
      scoped_village_id: null
    });
    setShowAddAdminModal(true);
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin({
      id: admin.id,
      name: admin.name,
      phone_number: admin.phone_number,
      role_id: admin.role?.id || null,
      scoped_village_id: admin.scoped_village_id || admin.scoped_village?.id || null
    });
    setShowAddAdminModal(true);
  };

  const handleDeleteAdmin = (admin) => {
    setSelectedAdmin(admin);
    setShowConfirmDeleteModal(true);
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    
    const formData = {
      name: selectedAdmin.name,
      phone_number: selectedAdmin.phone_number,
      role_id: selectedAdmin.role_id,
      scoped_village_id: selectedAdmin.scoped_village_id ? Number(selectedAdmin.scoped_village_id) : null
    };

    if (selectedAdmin?.id) {
      // Update existing admin
      updateAdmin.mutate(
        { id: selectedAdmin.id, data: formData },
        {
          onSuccess: () => {
            toast.success('Admin updated successfully');
            handleModalClose();
          },
          onError: (error) => {
            const apiMessage = getApiErrorMessage(error);
            toast.error(apiMessage || 'Failed to update admin');
          },
        }
      );
    } else {
      // Create new admin
      createAdmin.mutate(formData, {
        onSuccess: () => {
          toast.success('Admin added successfully');
          handleModalClose();
        },
        onError: (error) => {
          const apiMessage = getApiErrorMessage(error);
          toast.error(apiMessage || 'Failed to create admin');
        },
      });
    }
  };

  const handleConfirmDelete = () => {
    if (selectedAdmin) {
      deleteAdmin.mutate(selectedAdmin.id, {
        onSuccess: () => {
          toast.success('Admin deleted successfully');
          setShowConfirmDeleteModal(false);
        },
        onError: (error) => {
          const apiMessage = getApiErrorMessage(error);
          toast.error(apiMessage || 'Failed to delete admin');
        },
      });
    }
  };

  const handleModalClose = () => {
    setShowAddAdminModal(false);
    setSelectedAdmin(null);
  };

  const columns = [
    {
      key: 'name',
      label: 'Admin',
      sortable: true,
      render: (admin) => <AvatarName name={admin.name} email={admin.phone_number} />
    },
    {
      key: 'contact',
      label: 'Contact',
      render: (admin) => <ContactInfo phone={admin.phone_number} email={'-'} />
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (admin) => <RoleBadge role={admin.role} />
    },
    {
      key: 'created_at',
      label: 'Date Created',
      sortable: true,
      render: (admin) => formatDateDMY(admin.created_at)
    },
    {
      key: 'last_login_at',
      label: 'Last Login',
      sortable: true,
      render: (admin) => admin.updated_at ? formatDateDMY(admin.updated_at) : 'Never'
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (admin) => (
        <div className="flex items-center space-x-2">
          {can(PERMISSIONS.EDIT_ADMIN) && (
          <button
            onClick={() => handleEditAdmin(admin)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="Edit Admin"
          >
            <Edit size={16} />
          </button>
          )}
          {can(PERMISSIONS.DELETE_ADMIN) && (
          <button
            onClick={() => handleDeleteAdmin(admin)}
            className="text-red-600 hover:text-red-800 p-1"
            title="Delete Admin"
          >
            <Trash size={16} />
          </button>
          )}
        </div>
      )
    }
  ];

  const { data: profile } = useGetProfile();
  const userRole = profile?.data?.current_role || profile?.data?.role?.name || profile?.data?.user?.role?.name;
  const isVillageAdmin = userRole === 'village_admin' && !isAdmin;

  if (!isAdmin || isVillageAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_ADMINS}>
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Admin Management</h1>
            <p className="text-gray-600 mt-1">Manage system administrators and their permissions</p>
          </div>
          <PermissionGate permission={PERMISSIONS.CREATE_ADMIN}>
          <button
            onClick={handleAddAdmin}
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center gap-2"
          >
            <Plus size={20} />
            Add Admin
          </button>
          </PermissionGate>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Admins</p>
                <p className="text-2xl font-bold text-gray-900">{adminsData?.total || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search admins..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow">
          <Table
            data={adminsData?.data || []}
            columns={columns}
            isLoading={isLoadingAdmins}
            onSort={handleSort}
            sortField={sortField}
            sortDirection={sortDirection}
            currentPage={page}
            totalPages={adminsData?.last_page || 1}
            onPageChange={setPage}
            totalItems={adminsData?.total || 0}
            itemsPerPage={adminsData?.per_page || 15}
          />
        </div>
      </div>

      {/* Add/Edit Admin Modal */}
      <Modal
        isOpen={showAddAdminModal}
        onClose={handleModalClose}
        title={selectedAdmin?.id ? 'Edit Admin' : 'Add New Admin'}
      >
        <form onSubmit={handleModalSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Select Member *
              </label>
              <Select
                options={(members || []).map(member => ({
                  value: member.id,
                  label: `${member.name} (${member.phone_number})`,
                }))}
                value={
                  selectedAdmin?.member_id
                    ? {
                        value: selectedAdmin.member_id,
                        label: `${selectedAdmin.name} `,
                      }
                    : null
                }
                onChange={(option) => {
                  if (!option) {
                    setSelectedAdmin(prev => ({
                      ...prev,
                      member_id: null,
                      name: '',
                      phone_number: '',
                    }));
                    return;
                  }
                  const member = members.find(m => m.id === option.value);
                  if (member) {
                    setSelectedAdmin(prev => ({
                      ...prev,
                      member_id: member.id,
                      name: member.name,
                      phone_number: member.phone_number,
                      scoped_village_id: prev?.scoped_village_id || member.scoped_village_id || member.village_id || null,
                    }));
                  }
                }}
                placeholder="Select member..."
                isClearable
                className="w-full"
                classNamePrefix="react-select"
                styles={{
                  menuList: (base) => ({
                    ...base,
                    maxHeight: "180px",
                    overflowY: "auto",
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                required
                value={selectedAdmin?.phone_number || ''}
                onChange={(e) => setSelectedAdmin(prev => ({ ...prev, phone_number: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter phone number"
                disabled
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            {(() => {
              const rawRoles = Array.isArray(rolesData?.data)
                ? rolesData.data
                : (Array.isArray(rolesData?.data?.data)
                  ? rolesData.data.data
                  : (Array.isArray(rolesData) ? rolesData : []));

              const currentRole = rawRoles.find(role => role.id === selectedAdmin?.role_id);

              return (
                <>
                  <Select
                    isLoading={isLoadingRoles}
                    value={currentRole ? {
                      value: currentRole.id,
                      label: currentRole.display_name || currentRole.name
                    } : null}
                    onChange={(option) => {
                      const selectedRole = rawRoles.find(r => r.id === option?.value);
                      setSelectedAdmin(prev => {
                        let scopedVillage = prev?.scoped_village_id;
                        if (selectedRole?.name === 'village_admin' && !scopedVillage && prev?.member_id) {
                          const member = members.find(m => m.id === prev.member_id);
                          scopedVillage = member?.scoped_village_id || member?.village_id || null;
                        }
                        return {
                          ...prev,
                          role_id: option?.value || null,
                          scoped_village_id: scopedVillage || null
                        };
                      });
                    }}
                    options={rawRoles.map(role => ({
                      value: role.id,
                      label: role.display_name || role.name
                    }))}
                    placeholder={isLoadingRoles ? "Loading roles..." : "Select a role"}
                    className="w-full"
                    classNamePrefix="react-select"
                    menuPosition="fixed"
                    styles={{
                      container: (base) => ({ ...base, position: "relative" }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: "180px",
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                      }),
                    }}
                    isClearable
                  />

                  {villages.length > 0 && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Scoped Village / Region {currentRole?.name === 'village_admin' ? '(Recommended for Village Admin)' : '(Optional Scope)'}
                      </label>
                      <select
                        value={selectedAdmin?.scoped_village_id || ''}
                        onChange={(e) => setSelectedAdmin(prev => ({ ...prev, scoped_village_id: e.target.value ? Number(e.target.value) : null }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">-- All Villages / Regions (Full Access) --</option>
                        {villages.map(v => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {currentRole?.name === 'village_admin' && !selectedAdmin?.scoped_village_id
                          ? 'Selecting a specific village restricts this admin to managing only that village.'
                          : 'If selected, this admin will only be able to view members and contributions from this specific village.'}
                      </p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleModalClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createAdmin.isPending || updateAdmin.isPending}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
            >
              {(createAdmin.isPending || updateAdmin.isPending) ? 'Saving...' : (selectedAdmin?.id ? 'Update Admin' : 'Add Admin')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showConfirmDeleteModal}
        onClose={() => setShowConfirmDeleteModal(false)}
        title="Delete Admin"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete <strong>{selectedAdmin?.name}</strong>? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowConfirmDeleteModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={deleteAdmin.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {deleteAdmin.isPending ? 'Deleting...' : 'Delete Admin'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
    </PermissionPage>
  );
};

export default AdminsPage; 